import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { isAccountsStoreInitialized, getOWAConnectedAccounts } from 'owa-accounts-store';
import { isEdu } from 'owa-nonboot-userconfiguration-manager';
import loc from 'owa-localize';
import {
    accountProviderSelectionInstructions,
    accountProviderSelectionInstructionsForSchool,
    accountProviderListHeader,
    connectedAccountsHeader,
    removeAccountHotReload,
    accountIsBeingAddedLabel,
    accountAddFailedLabel,
    checkingAccountsLoading,
} from './AccountsEditor.locstring.json';
import AccountProvider from './AccountProvider';
import ConnectedAccount from './ConnectedAccount';
import { maxNumberConnectedAccount } from '../constants';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import getAccountsEditorState from '../selectors/getAccountsEditorState';
import AccountProviderInfo from '../store/schema/AccountProviderInfo';
import { AccountsEditorState } from '../store/schema/AccountsEditorState';
import getAccountProviders from '../utils/getAccountProviders';
import { lazyGetMergeViewEnabledAccount } from 'owa-truetime';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { AccountsEditorSource } from '../store/schema/AccountsEditorSource';
import setAccountsEditorSource from '../mutators/setAccountsEditorSource';

const styles = require('./AccountsEditor.scss');

export interface AccountsEditorProps {
    source: AccountsEditorSource;
}

export default observer(function AccountsEditor(props: AccountsEditorProps) {
    const showLoadingSpinner = !isAccountsStoreInitialized();
    const accountProviders = getAccountProviders();
    const owaConnectedAccounts = getOWAConnectedAccounts();
    const accountsEditorState = getAccountsEditorState();

    const showProviderSelection =
        owaConnectedAccounts.length < maxNumberConnectedAccount && accountProviders.length > 0;
    const showEditAccounts = owaConnectedAccounts.length > 0;

    React.useEffect(() => {
        setAccountsEditorSource(props.source);
    }, [props.source]);

    return showLoadingSpinner ? (
        <div className={styles.spinnerContainer}>
            <Spinner className={styles.pageLoadingSpinner} size={SpinnerSize.large} />
            <div className={styles.loadingLabel}>{loc(checkingAccountsLoading)}</div>
        </div>
    ) : (
        <>
            {renderNotificationBar(accountsEditorState)}
            {showProviderSelection && renderProviderSelectionContent(accountProviders)}
            {showEditAccounts && renderEditAccountsContent()}
        </>
    );
});

function renderEditAccountsContent() {
    const owaConnectedAccounts = getOWAConnectedAccounts();
    const getMergeViewEnabledAccount = lazyGetMergeViewEnabledAccount.tryImportForRender();
    const mergeViewEnabledAccount = getMergeViewEnabledAccount
        ? getMergeViewEnabledAccount()
        : undefined;

    return (
        <div>
            <p className={styles.editHeader}>{loc(connectedAccountsHeader)}</p>
            {owaConnectedAccounts &&
                owaConnectedAccounts.map(account => (
                    <ConnectedAccount
                        key={account.accountId}
                        account={account}
                        mergeViewEnabledAccount={mergeViewEnabledAccount}
                    />
                ))}
        </div>
    );
}

function renderProviderSelectionContent(accountProviders: AccountProviderInfo[]) {
    return (
        <div className={styles.providerSelection}>
            <div>
                <p className={styles.providerSelectionInstructions}>
                    {isEdu()
                        ? loc(accountProviderSelectionInstructionsForSchool)
                        : loc(accountProviderSelectionInstructions)}
                </p>
            </div>
            <div className={styles.panelContainer}>
                <div className={styles.accountProviderHeader}>{loc(accountProviderListHeader)}</div>
                <div className={styles.accountProviderOptions}>
                    {accountProviders.map(accountProvider => (
                        <AccountProvider
                            key={accountProvider.accountProviderType}
                            accountProviderInfo={accountProvider}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function renderNotificationBar(accountsEditorState: AccountsEditorState): JSX.Element {
    const { type, content } = getNotificationContent(accountsEditorState);

    if (content) {
        return <MessageBar messageBarType={type}>{content}</MessageBar>;
    }

    return null;
}

function getNotificationContent(
    accountsEditorState: AccountsEditorState
): { type: MessageBarType; content: JSX.Element } {
    switch (accountsEditorState) {
        case AccountsEditorState.Adding:
            return {
                type: MessageBarType.info,
                content: <>{loc(accountIsBeingAddedLabel)}</>,
            };
        case AccountsEditorState.Removed:
            return {
                type: MessageBarType.warning,
                content: <Link onClick={reloadPage}>{loc(removeAccountHotReload)}</Link>,
            };
        case AccountsEditorState.AddFailed:
            return {
                type: MessageBarType.error,
                content: <>{loc(accountAddFailedLabel)} </>,
            };
        default:
            return { type: null, content: null };
    }
}

function reloadPage() {
    location.reload();
}
